import {
  type SIWESession,
  type SIWEVerifyMessageArgs,
  type SIWECreateMessageArgs,
  createSIWEConfig,
  formatMessage,
} from '@reown/appkit-siwe';
import { getAddress } from 'viem';

import {
  TokenPresaleClient,
  Web3SignInRequest,
} from 'common/api/autogenerated/token-presale';

const tokenPresaleApiClient = new TokenPresaleClient({
  BASE: '/api/token-presale',
  WITH_CREDENTIALS: true,
});

export const getNonce = () => tokenPresaleApiClient.web3Auth.postV1Web3Nonce();
export const getUser = () => tokenPresaleApiClient.user.getV1UsersMe();
export const postWeb3SignIn = (params: Web3SignInRequest) =>
  tokenPresaleApiClient.web3Auth.postV1Web3Signin(params);
export const postWeb3SignOut = () =>
  tokenPresaleApiClient.web3Auth.postV1Web3Signout();

const normalizeAddress = (address: string): string => {
  try {
    const splitAddress = address.split(':');
    const extractedAddress = splitAddress[splitAddress.length - 1];
    const checksumAddress = getAddress(extractedAddress);
    splitAddress[splitAddress.length - 1] = checksumAddress;
    const normalizedAddress = splitAddress.join(':');

    return normalizedAddress;
  } catch (error) {
    console.warn(error);
    return address;
  }
};

async function getSession(): Promise<SIWESession | null> {
  const response = await getUser();

  if (!response.blockchainAddress)
    throw new Error('Network response was not ok');

  return { address: response.blockchainAddress, chainId: 1 };
}

const verifyMessage = async ({ message, signature }: SIWEVerifyMessageArgs) => {
  const response = await postWeb3SignIn({ message, signature });

  return !!response.address;
};

const signOut = async (): Promise<boolean> => {
  await postWeb3SignOut();

  window.postMessage({ type: 'web3:signout:success' }, '*');

  return true;
};

const onSignIn = () => {
  window.postMessage({ type: 'web3:signin:success' }, '*');
};

const getNonceCallback = async () => {
  const nonce = await getNonce();
  if (!nonce.data) throw new Error('Failed to get nonce!');
  return nonce.data;
};

const createMessage = ({ address, ...args }: SIWECreateMessageArgs): string =>
  formatMessage(args, normalizeAddress(address));

const getMessageParams = async () => ({
  domain: window.location.host,
  uri: window.location.origin,
  chains: [1],
  statement: 'Please sign with your account',
});

const siweConfig = createSIWEConfig({
  getNonce: getNonceCallback,
  getMessageParams,
  createMessage,
  getSession,
  verifyMessage,
  onSignIn,
  signOut,
  signOutOnDisconnect: true,
  signOutOnAccountChange: false,
  signOutOnNetworkChange: false,
});

export default siweConfig;
